<script setup lang="ts">

import { onMounted, nextTick, ref } from 'vue';
import { RouterView } from 'vue-router'; 
import { useRouter } from "@library/Services";
import { useHttp } from '@library/Security';
import { Loading } from '@app/Components/Layout';
import { useJupiterProps, JupiterHeader, JupiterFooter } from '@modules/Jupiter';
import { useTheme } from '@services/Theme';
import { isLocal } from '@utilities/Environments';
import { useIsHostedInBrowser } from "@modules/Jupiter"

const http = useHttp();
const router = useRouter();
const isHostedInBrowser = useIsHostedInBrowser();
const hasCompletedFirstPageLoad = ref( false );

const jupiterProps = useJupiterProps();
let currentRouteAnimation = '';

/**
 * On mounted, wait for the JupiterProps
 */
onMounted( () =>
{

    // Initialise the theme - this must be called inside a setup function
    const theme = useTheme();
    theme.initialise();
    theme.setTheme( 'theme-default');
    theme.setDarkMode( true );
    nextTick( () =>
    {
        document.body.classList.add( 'ready' );
    } );

} );

/**
 * Before router leave, disable scrollbars to prevent screen bouncing
 */
function onBeforeRouteLeave()
{
    document.querySelector( '.main-scroll-content' )?.classList.add( 'navigation-active' );
}

/**
 * After route complete, enable scrollbars 
 */
function onAfterRouteEnter()
{
    document.querySelector( '.main-scroll-content' )?.classList.remove( 'navigation-active' );
    hasCompletedFirstPageLoad.value = true;
}

/**
 * Gets the transition animation for the route
 * @param {*} route 
 */
function transitionAnimation( route: any )
{

    let animation = 'default';

    if( router.navigation.isBackNavigation )
    {
        animation = currentRouteAnimation;
    }

    else
    {
        animation = route.meta?.animation ?? 'default';
        currentRouteAnimation = animation;
    }

    const backState = router.navigation.isBackNavigation ? 'router-back ' : '';
    return backState + animation;

}

</script>

<template>

    <div v-if="jupiterProps.isReady"
         class="flex flex-col w-full h-full transition-opacity 
                3xl:max-w-xl 3xl:m-auto
                {{ `duration-${screenState.transitionDuration}` }} ease-in"
         :class="{ 
             'loading': http.isLoading || router.isNavigating.value, 
             'opacity-0': jupiterProps.isInitialising || hasCompletedFirstPageLoad == false,
             'saturate-[90%]': isLocal() // Matches closer to mobile device palette
         }">
      
        <div v-if="isHostedInBrowser">
            <JupiterHeader />
        </div>

        <div class="app-frame w-full h-full overflow-hidden">
                                
            <div class="w-full h-full overflow-hidden"> 

                <RouterView v-slot="{ Component, route }">
                    <Transition :name="transitionAnimation( route )"
                                @before-leave="onBeforeRouteLeave()"
                                @after-enter="onAfterRouteEnter()">
                        <div class="router-content w-full h-full overflow-hidden" :key="route.path">
                            <Suspense :key="route.path" :timeout="5000">
                                <template #default>
                                    <div class="apple-fix w-full h-full static">
                                        <Component :is="Component" :key="route.path" />
                                    </div>
                                </template>
                                <template #fallback>
                                    <div class="w-full h-full">
                                        <Loading />
                                    </div>   
                                </template>
                            </Suspense>
                        </div>
                    </Transition>
                </RouterView>

            </div>

            <BackButton />

        </div>

        <div v-if="isHostedInBrowser" 
             class="flex justify-center z-50 w-full">
            <JupiterFooter />
        </div>
        
    </div>

</template>
